import { useEffect, useRef } from "react";
import type { GeneratorConfig } from "@/types/generator";

type ModalState = "closed" | "processing" | "ready" | "success";

export function useTabProcessingState(
  modalState: ModalState,
  config: GeneratorConfig | null
) {
  const originalTitle = config?.meta_title || config?.display_name || "";
  const faviconPath = config?.favicon_path || "/favicon.png";
  const prevStateRef = useRef<ModalState>("closed");
  const animFrameRef = useRef<number | null>(null);
  const angleRef = useRef(0);

  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = modalState;

    if (modalState === "processing") {
      // --- Title ---
      document.title = `Generating... | ${originalTitle}`;

      // --- Animated favicon ---
      const canvas = document.createElement("canvas");
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext("2d")!;
      const link =
        (document.querySelector("link[rel*='icon']") as HTMLLinkElement) ||
        (() => {
          const l = document.createElement("link");
          l.rel = "icon";
          document.head.appendChild(l);
          return l;
        })();

      const startTime = performance.now();
      const FILL_DURATION = 25_000;

      const draw = () => {
        const elapsed = performance.now() - startTime;
        const raw = Math.min(elapsed / FILL_DURATION, 0.95);
        const progress = 1 - Math.pow(1 - raw, 2);

        ctx.clearRect(0, 0, 32, 32);

        // Dark background circle
        ctx.beginPath();
        ctx.arc(16, 16, 15, 0, Math.PI * 2);
        ctx.fillStyle = "#1e293b";
        ctx.fill();

        // Progress pie wedge
        const startAngle = -Math.PI / 2;
        const endAngle = startAngle + Math.PI * 2 * progress;
        ctx.beginPath();
        ctx.moveTo(16, 16);
        ctx.arc(16, 16, 15, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = "#3b82f6";
        ctx.fill();

        // Inner cutout (donut)
        ctx.beginPath();
        ctx.arc(16, 16, 9, 0, Math.PI * 2);
        ctx.fillStyle = "#1e293b";
        ctx.fill();

        // White percentage text
        const pct = Math.round(progress * 100);
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 10px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(`${pct}`, 16, 17);

        link.href = canvas.toDataURL("image/png");
        animFrameRef.current = requestAnimationFrame(draw);
      };
      animFrameRef.current = requestAnimationFrame(draw);

      return () => {
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      };
    }

    if (modalState === "ready" && prev === "processing") {
      // --- Restore favicon ---
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      if (link) link.href = faviconPath;

      // --- Title flash ---
      document.title = `Ready! | ${originalTitle}`;
      const timer = setTimeout(() => {
        document.title = originalTitle;
      }, 3000);

      // --- Audio cue (only if tab is hidden) ---
      if (document.hidden) {
        try {
          const ac = new AudioContext();
          const playNote = (freq: number, start: number, dur: number) => {
            const osc = ac.createOscillator();
            const gain = ac.createGain();
            osc.type = "sine";
            osc.frequency.value = freq;
            gain.gain.value = 0.15;
            gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + start + dur);
            osc.connect(gain).connect(ac.destination);
            osc.start(ac.currentTime + start);
            osc.stop(ac.currentTime + start + dur);
          };
          playNote(587.33, 0, 0.15); // D5
          playNote(880, 0.15, 0.2);   // A5
        } catch {
          // AudioContext may be blocked; silently ignore
        }
      }

      return () => clearTimeout(timer);
    }

    if (modalState === "closed" || modalState === "success") {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      if (link) link.href = faviconPath;
      if (originalTitle) document.title = originalTitle;
    }
  }, [modalState, originalTitle, faviconPath]);
}
